<template>
    <div class="pt-2">
        <div v-if="team">
            <div class="rounded-lg border border-gray-400 bg-gray-100 overflow-hidden mb-8">
                <div class="py-4 px-6 bg-gray-500">
                    <h2 class="text-sm uppercase tracking-widest text-white">
                        {{ is_owner ? 'Team Leader' : 'Team Membership' }}
                    </h2>
                </div>
                <div class="px-8 py-6">
                    <div class="text-xl font-semibold text-blue-700">{{ team.owner.name }}</div>
                    <div class="text-gray-600 mt-1">{{ team.owner.email }}</div>
                    <div v-if="is_owner" class="text-sm uppercase tracking-wider text-gray-600 mt-4">
                        {{ team.seats_used }} of {{ team.seat_limit }} seats used
                        <span class="mx-2">·</span>
                        {{ team.seats_available }} available
                    </div>

                    <button
                        v-else
                        type="button"
                        class="block ml-auto mt-4 text-sm text-red-600 hover:text-red-500"
                        :disabled="busy"
                        @click="leaveTeam"
                    >
                        Leave Team
                    </button>
                </div>
            </div>

            <div v-if="is_owner" class="rounded-lg border border-gray-400 bg-white p-6 mb-8">
                <h2 class="text-lg font-semibold text-gray-800 mb-2">Invite a Team Member</h2>
                <p class="text-sm text-gray-600 mb-4">
                    Invited users will receive an email to create or link their account.
                </p>
                <form class="flex flex-col sm:flex-row gap-3" @submit.prevent="invite">
                    <input
                        v-model="email"
                        type="email"
                        class="field flex-1"
                        placeholder="team-member@example.com"
                        required
                        :disabled="busy || team.seats_available < 1"
                    >
                    <button
                        type="submit"
                        class="border text-white uppercase font-semibold tracking-wider text-xs px-6 py-3 bg-blue-500 border-blue-500 hover:bg-blue-600"
                        :disabled="busy || team.seats_available < 1"
                    >
                        Send Invitation
                    </button>
                </form>
                <div v-if="error" class="text-red-500 text-sm mt-3">{{ error }}</div>
                <div v-if="team.seats_available < 1" class="text-red-500 text-sm mt-3">
                    This team has no remaining seats.
                </div>
            </div>

            <div>
                <h2 class="text-lg font-semibold text-gray-800 pb-6">Team Members</h2>

                <div v-if="memberships.length === 0" class="text-gray-600">
                    No team members yet.
                </div>

                <div v-else class="overflow-x-auto rounded-lg border border-gray-400 bg-white">
                    <table class="w-full">
                        <thead class="bg-gray-warm-100 border-b border-gray-400">
                            <tr>
                                <th class="font-semibold text-left text-xs uppercase tracking-wider text-gray-600 px-5 py-4">Name</th>
                                <th class="font-semibold text-left text-xs uppercase tracking-wider text-gray-600 px-5 py-4">Email</th>
                                <th
                                    v-if="is_owner"
                                    class="font-semibold text-left text-xs uppercase tracking-wider text-gray-600 px-5 py-4"
                                >
                                    Status
                                </th>
                                <th
                                    v-if="is_owner"
                                    class="font-semibold text-left text-xs uppercase tracking-wider text-gray-600 px-5 py-4"
                                >
                                    Editability
                                </th>
                                <th
                                    v-if="is_owner"
                                    class="font-semibold text-right text-xs uppercase tracking-wider text-gray-600 px-5 py-4"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="membership in memberships"
                                :key="membership.id"
                                class="border-b border-gray-300 last:border-b-0"
                            >
                                <td class="font-semibold text-gray-800 px-5 py-4 whitespace-nowrap">
                                    {{ membership.user ? membership.user.name : '—' }}
                                </td>
                                <td class="text-sm text-gray-600 px-5 py-4 whitespace-nowrap">
                                    {{ membership.email }}
                                </td>
                                <td v-if="is_owner" class="px-5 py-4 whitespace-nowrap">
                                    <span
                                        class="uppercase tracking-wider text-xs font-semibold"
                                        :class="membership.status === 'accepted' ? 'text-green-warm-600' : 'text-blue-600'"
                                    >
                                        {{ membership.status === 'accepted' ? 'Accepted' : 'Invitation Pending' }}
                                    </span>
                                </td>
                                <td v-if="is_owner" class="text-sm text-gray-700 px-5 py-4 whitespace-nowrap">
                                    <label v-if="membership.status === 'accepted'" class="flex items-center">
                                        <input
                                            v-model="membership.is_editable"
                                            type="checkbox"
                                            class="mr-2"
                                            :disabled="busy"
                                            @change="setEditable(membership)"
                                        >
                                        Editable
                                    </label>
                                    <span v-else>—</span>
                                </td>
                                <td v-if="is_owner" class="px-5 py-4 whitespace-nowrap">
                                    <div class="flex items-center justify-end gap-4">
                                        <button
                                            v-if="membership.status === 'pending'"
                                            type="button"
                                            class="text-sm text-blue-600 hover:text-blue-500"
                                            :disabled="busy"
                                            @click="resend(membership)"
                                        >
                                            Resend
                                        </button>
                                        <button
                                            type="button"
                                            class="text-sm text-red-600 hover:text-red-500"
                                            :disabled="busy"
                                            @click="revoke(membership)"
                                        >
                                            {{ membership.status === 'pending' ? 'Revoke Invitation' : 'Remove Member' }}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div v-else class="rounded-lg border border-gray-400 bg-gray-100 px-8 py-8">
            <h2 class="text-xl font-semibold text-gray-800 mb-2">No team account</h2>
            <p class="text-gray-600 mb-0">
                Upgrade to a team plan to invite and manage team members.
            </p>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        is_owner: { default: false },
        team: { default: null },
        initial_memberships: { default: () => [] },
    },

    data() {
        return {
            email: '',
            memberships: this.initial_memberships.data || this.initial_memberships || [],
            busy: false,
            error: null,
        };
    },

    methods: {
        applyPayload(response) {
            this.team.seat_limit = response.data.team.seat_limit;
            this.team.seats_used = response.data.team.seats_used;
            this.team.seats_available = response.data.team.seats_available;
            this.memberships = response.data.memberships.data || response.data.memberships;
        },
        requestError(error) {
            this.error = error?.response?.data?.errors
                ? Object.values(error.response.data.errors).flat()[0]
                : 'Unable to update the team. Please try again.';
        },
        invite() {
            this.busy = true;
            this.error = null;
            axios.post('/broker/settings/team', { email: this.email })
                .then(response => {
                    this.applyPayload(response);
                    this.email = '';
                })
                .catch(this.requestError)
                .finally(() => this.busy = false);
        },
        setEditable(membership) {
            const isEditable = membership.is_editable;
            this.busy = true;
            this.error = null;
            axios.patch('/broker/settings/team/' + membership.id, { is_editable: isEditable })
                .then(this.applyPayload)
                .catch(error => {
                    this.requestError(error);
                    membership.is_editable = ! isEditable;
                })
                .finally(() => this.busy = false);
        },
        resend(membership) {
            this.busy = true;
            this.error = null;
            axios.post('/broker/settings/team/' + membership.id + '/resend')
                .then(this.applyPayload)
                .catch(this.requestError)
                .finally(() => this.busy = false);
        },
        revoke(membership) {
            const label = membership.status === 'pending' ? 'invitation' : 'team member';
            const action = membership.status === 'pending' ? 'Revoke' : 'Remove';
            if (! confirm(action + ' this ' + label + '?')) {
                return;
            }

            this.busy = true;
            this.error = null;
            axios.delete('/broker/settings/team/' + membership.id)
                .then(this.applyPayload)
                .catch(this.requestError)
                .finally(() => this.busy = false);
        },
        leaveTeam() {
            if (! confirm('Are you sure you want to leave this team?')) {
                return;
            }

            this.busy = true;
            axios.delete('/broker/settings/team')
                .then(() => window.location = '/broker/settings/account')
                .catch(this.requestError)
                .finally(() => this.busy = false);
        },
    },
};
</script>
